import React, { useState } from 'react';
import { TerminalSquare, ChevronDown, ChevronUp, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { testJobTreadConnection, getJobTreadCustomFields } from '../../services/jobtreadService';
import { resolveJobTreadFieldMappings } from '../../services/jobtreadFieldMapping';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Page-level dev-mode integration summary for the Sales/Finance/Marketing
 * dashboards: connection state, QuickBooks sync/parse diagnostics, the
 * JobTread org and custom-field mapping in effect, and the fetch log for the
 * page. Only mounted when dev mode is on (see useDevMode).
 */

const Pill = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
    ok ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {label}
  </span>
);

const Section = ({ title, children }) => (
  <div className="mt-3">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300 mb-1">{title}</h4>
    {children}
  </div>
);

const DevModePanel = ({ debug, page }) => {
  const { currentUser } = useAuth();
  const [open, setOpen] = useState(true);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [customFields, setCustomFields] = useState(null);
  const [loadingCf, setLoadingCf] = useState(false);
  const [remapping, setRemapping] = useState(false);

  if (!debug) return null;

  const qb = debug.connections?.quickbooks || {};
  const jt = debug.connections?.jobtread || {};
  const ga4 = debug.connections?.ga4;
  const mapping = jt.fieldMappings;
  const fetches = debug.fetches || [];
  const failedFetches = fetches.filter((f) => f.status === 'rejected');

  const runConnectionTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await testJobTreadConnection();
      setTestResult('Grant key valid — JobTread API reachable.');
    } catch (e) {
      setTestResult(`FAILED: ${e?.message || e}`);
    } finally {
      setTesting(false);
    }
  };

  const loadCustomFields = async () => {
    if (!jt.orgId) return;
    setLoadingCf(true);
    try {
      const data = await getJobTreadCustomFields(jt.orgId);
      const nodes = data?.organization?.customFields?.nodes ?? [];
      setCustomFields((Array.isArray(nodes) ? nodes : [nodes]).filter(Boolean));
    } catch (e) {
      setCustomFields([{ id: '—', name: `Failed to load: ${e?.message || e}`, type: '', targetType: '' }]);
    } finally {
      setLoadingCf(false);
    }
  };

  const remapFields = async () => {
    if (!jt.orgId || !currentUser?.uid) return;
    setRemapping(true);
    try {
      await resolveJobTreadFieldMappings(currentUser.uid, jt.orgId, { forceRefresh: true });
      window.location.reload();
    } catch (_) {
      setRemapping(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border-2 border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10 p-4 text-xs font-mono">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-center gap-2 text-amber-800 dark:text-amber-300 font-semibold"
      >
        <TerminalSquare className="w-4 h-4" />
        <span className="text-center">Dev Diagnostics — {page}</span>
        {failedFetches.length > 0 && <AlertTriangle className="w-4 h-4 text-red-500" />}
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="mt-3 text-gray-800 dark:text-gray-200">
          {/* Identity */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-[11px]">
            <span>uid: {debug.uid}</span>
            <span>accountId: {debug.companyId || '— (none resolved)'}</span>
          </div>

          {/* QuickBooks */}
          <Section title="QuickBooks">
            <div className="flex flex-wrap items-center gap-2">
              <Pill ok={!!qb.connected} label={qb.connected ? 'connected' : 'not connected'} />
              {qb.integrationDocId && <span className="text-[11px]">integration doc: {qb.integrationDocId}</span>}
              {qb.lastRevenueSyncAt && (
                <span className="text-[11px]">revenue cache synced {qb.lastRevenueSyncAt} ({qb.lastSyncSource})</span>
              )}
            </div>
            {qb.parseDiagnostics?.monthsMissingIncomeRow?.length > 0 && (
              <div className="text-red-600 dark:text-red-400 mt-1">
                ⚠ P&L income row missing for: {qb.parseDiagnostics.monthsMissingIncomeRow.join(', ')}
              </div>
            )}
            {qb.plDiagnostics && (
              <div className="mt-1 text-[11px]">
                P&L rows found: {Object.entries(qb.plDiagnostics.rowsFound || {}).map(([k, v]) => `${k}:${v ? '✓' : '✗'}`).join('  ')}
                {qb.plDiagnostics.reportPeriod?.basis && <span> · basis: {qb.plDiagnostics.reportPeriod.basis}</span>}
                {(qb.plDiagnostics.warnings || []).map((w, i) => (
                  <div key={i} className="text-red-600 dark:text-red-400">⚠ {w}</div>
                ))}
              </div>
            )}
            {qb.balanceDiagnostics && (
              <div className="mt-1 text-[11px]">
                Balance sheet rows found: {Object.entries(qb.balanceDiagnostics.rowsFound || {}).map(([k, v]) => `${k}:${v ? '✓' : '✗'}`).join('  ')}
                {qb.balanceDiagnostics.cashMethod && <span> · cash via {qb.balanceDiagnostics.cashMethod}</span>}
                {(qb.balanceDiagnostics.warnings || []).map((w, i) => (
                  <div key={i} className="text-red-600 dark:text-red-400">⚠ {w}</div>
                ))}
              </div>
            )}
          </Section>

          {/* JobTread */}
          <Section title="JobTread">
            <div className="flex flex-wrap items-center gap-2">
              <Pill ok={!!jt.connected} label={jt.connected ? 'connected (grant key)' : 'not connected'} />
              {jt.orgId && (
                <span className="text-[11px]">
                  org: {jt.orgName || '—'} ({jt.orgId}){jt.multipleOrgs ? ` · ⚠ ${jt.orgCount} orgs available, first used` : ''}
                </span>
              )}
              {jt.connected && (
                <button
                  type="button"
                  onClick={runConnectionTest}
                  disabled={testing}
                  className="px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-[10px] hover:bg-amber-300"
                >
                  {testing ? 'testing…' : 'Test connection'}
                </button>
              )}
            </div>
            {testResult && <div className={`mt-1 ${testResult.startsWith('FAILED') ? 'text-red-600' : 'text-emerald-700 dark:text-emerald-400'}`}>{testResult}</div>}

            {mapping && (
              <div className="mt-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[11px]">Custom-field mapping ({mapping.resolvedBy}, {mapping.resolvedAt?.slice(0, 10)})</span>
                  <button
                    type="button"
                    onClick={remapFields}
                    disabled={remapping}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-900 dark:text-amber-100 text-[10px] hover:bg-amber-300"
                  >
                    <RefreshCw className={`w-3 h-3 ${remapping ? 'animate-spin' : ''}`} /> re-map
                  </button>
                </div>
                <table className="mt-1 text-[11px] w-full max-w-lg">
                  <tbody>
                    {Object.entries(mapping.fields || {}).map(([concept, f]) => (
                      <tr key={concept}>
                        <td className="pr-3 py-0.5 text-gray-600 dark:text-gray-400">{concept}</td>
                        <td className="py-0.5">
                          {f ? `→ "${f.fieldName}" (${f.fieldId})` : <span className="text-red-600 dark:text-red-400">unmapped</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {Object.keys(mapping.ambiguous || {}).length > 0 && (
                  <div className="text-red-600 dark:text-red-400 mt-1">
                    ⚠ ambiguous: {Object.entries(mapping.ambiguous).map(([k, v]) => `${k} (${v.map((c) => c.fieldName).join(' / ')})`).join('; ')} — pick one via re-map after renaming, or set manually.
                  </div>
                )}
                <div className="text-gray-500 dark:text-gray-400 mt-1">
                  {mapping.availableCustomFields?.length ?? 0} job custom fields available in the org
                  {jt.orgId && (
                    <button type="button" onClick={loadCustomFields} disabled={loadingCf} className="ml-2 underline hover:text-amber-700">
                      {loadingCf ? 'loading…' : (customFields ? 'reload list' : 'show list')}
                    </button>
                  )}
                </div>
                {customFields && (
                  <table className="mt-1 text-[10px] w-full max-w-lg">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="pr-2">name</th><th className="pr-2">type</th><th className="pr-2">target</th><th>id</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customFields.map((f) => (
                        <tr key={f.id}>
                          <td className="pr-2">{f.name}</td><td className="pr-2">{f.type}</td><td className="pr-2">{f.targetType}</td><td>{f.id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {jt.leadsDiagnostics && (
              <div className="mt-2 text-[11px]">
                jobs fetched: {jt.leadsDiagnostics.totalJobs}{jt.leadsDiagnostics.hasMoreJobs ? ' (more exist — first 100 only)' : ''} ·
                with value: {jt.leadsDiagnostics.jobsWithContractValue} ·
                with lead source: {jt.leadsDiagnostics.jobsWithLeadSource} ·
                with status: {jt.leadsDiagnostics.jobsWithStatus} ·
                with closedOn: {jt.leadsDiagnostics.jobsWithClosedOn} ·
                enriched query: {jt.leadsDiagnostics.enriched ? '✓' : `✗ (${jt.leadsDiagnostics.enrichError})`}
                {(jt.leadsDiagnostics.warnings || []).map((w, i) => (
                  <div key={i} className="text-red-600 dark:text-red-400">⚠ {w}</div>
                ))}
              </div>
            )}
            {jt.backlogDiagnostics && (
              <div className="mt-1 text-[11px]">
                backlog basis: {jt.backlogDiagnostics.backlogBasis} — active jobs value ${(jt.backlogDiagnostics.activeJobsValue ?? 0).toLocaleString()} vs proposal sum ${(jt.backlogDiagnostics.proposalSum ?? 0).toLocaleString()}
              </div>
            )}
          </Section>

          {/* GA4 (marketing only) */}
          {ga4 != null && (
            <Section title="Google Analytics">
              <Pill ok={!!ga4.connected} label={ga4.connected ? 'connected' : 'not connected'} />
            </Section>
          )}

          {/* Fetch log */}
          <Section title={`Fetch log (${fetches.length} calls, ${failedFetches.length} failed)`}>
            <table className="w-full text-[10px]">
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="pr-2">key</th><th className="pr-2">fn</th><th className="pr-2">status</th><th>error</th>
                </tr>
              </thead>
              <tbody>
                {fetches.map((f, i) => (
                  <tr key={i} className={f.status === 'rejected' ? 'text-red-600 dark:text-red-400' : ''}>
                    <td className="pr-2">{f.key}</td>
                    <td className="pr-2">{f.fn}</td>
                    <td className="pr-2">{f.status}</td>
                    <td className="break-all">{f.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>
      )}
    </div>
  );
};

export default DevModePanel;
